/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { useStore } from '../stores/common';
import { usePrimitiveStore } from '../stores/commonPrimitive';
import { Molecule } from '../models/Molecule';
import { Undoable } from './Undoable';
import { refuseWhileMinimizing } from '../view/engineSync';
import { cloneLabMoleculeLive } from './liveMoleculeClone';

// Cut the lab molecule at `index` onto the clipboard, as ONE undoable step.
// Shared by the context menu's Cut and the keyboard Ctrl/Cmd+X — the keyboard
// path used to splice the molecule out with no undo entry at all, so Ctrl+Z
// skipped the cut and reverted whatever came before it.
//
// Undo puts a copy back at the slot it came from and re-selects it; Redo removes
// THAT molecule by identity (not "whatever sits at the slot now") and re-stashes
// it as the cut clipboard. Returns whether anything was cut.
export const cutMoleculeWithUndo = (index: number): boolean => {
  const target = useStore.getState().projectState.testMolecules[index];
  if (!target) return false;
  if (refuseWhileMinimizing()) return false;
  // The live frame, not the saved one; a FULL clone so Undo brings the restraint,
  // damping and trajectory flag back with the molecule (the clipboard copy below
  // stays a default clone: Paste makes a NEW molecule somewhere else).
  let saved = cloneLabMoleculeLive(index) ?? Molecule.clone(target, true);
  let current: Molecule | null = target;

  const cut = () => {
    const i = current ? useStore.getState().projectState.testMolecules.indexOf(current) : -1;
    current = null;
    if (i < 0) return;
    // Re-snapshot on every cut, Redo included: the molecule may have moved since
    // the first cut (Undo, a run, Redo), and the next Undo — and the clipboard —
    // must have it where it was when it was cut THIS time, not at the first cut,
    // possibly on top of a molecule that has since moved into that spot.
    saved = cloneLabMoleculeLive(i) ?? saved;
    usePrimitiveStore.getState().set((state) => {
      state.cutMolecule = Molecule.clone(saved);
      state.copiedMolecule = null; // the latest Cut/Copy is what Paste gives
      // Selection indices hold list positions: once this molecule is spliced out
      // the next one slides into its slot and would inherit the highlight.
      state.pickedMoleculeIndex = -1;
      state.pickedMoleculeIndices = new Set<number>();
      state.pickedAtomIndex = -1;
      state.pickedAtomIndices = new Set<number>();
    });
    useStore.getState().set((state) => {
      state.projectState.testMolecules.splice(i, 1);
    });
    usePrimitiveStore.getState().setChanged(true);
  };

  const restore = () => {
    // A fresh clone each time: the store freezes what it holds, and `saved` is
    // kept for the next Undo.
    const m = Molecule.clone(saved, true);
    const at = Math.min(index, useStore.getState().projectState.testMolecules.length);
    useStore.getState().set((state) => {
      state.projectState.testMolecules.splice(at, 0, m);
    });
    current = m;
    usePrimitiveStore.getState().set((state) => {
      state.pickedMoleculeIndices = new Set<number>([at]);
    });
    usePrimitiveStore.getState().setChanged(true);
  };

  cut();
  const undoable: Undoable = { name: 'Cut Selected Molecule', timestamp: Date.now(), undo: restore, redo: cut };
  useStore.getState().addUndoable(undoable);
  const { loggable, logAction } = useStore.getState();
  if (loggable) logAction('Cut Selected Molecule');
  return true;
};
